import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

const SITE = "https://www.hitorstand.live";

function HeroSuit({ symbol, rotate = 0, top, left, right, opacity = 0.08 }) {
  return (
    <span
      aria-hidden="true"
      className="font-display"
      style={{
        position: "absolute",
        top,
        left,
        right,
        transform: `rotate(${rotate}deg)`,
        color: "var(--gold)",
        opacity,
        fontSize: "8rem",
        lineHeight: 1,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {symbol}
    </span>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language === "he" ? "he" : "en";
  const canonical = lng === "he" ? `${SITE}/?lng=he` : `${SITE}/`;

  return (
    <>
      <Helmet>
        <html lang={lng} dir={lng === "he" ? "rtl" : "ltr"} />
        <title>
          {lng === "he"
            ? "HitOrStand: מאמן בלקג׳ק חינמי, טבלת אסטרטגיה וספירת קלפים"
            : "HitOrStand: Free Blackjack Trainer, Strategy Chart & Card Counting"}
        </title>
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={`${SITE}/`} />
        <link rel="alternate" hrefLang="he" href={`${SITE}/?lng=he`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE}/`} />
        <meta
          name="description"
          content={
            lng === "he"
              ? "מאמן בלקג׳ק חינמי ללמוד ולתרגל אסטרטגיה בסיסית מושלמת. משוב מיידי על כל יד, מחשבון אסטרטגיה לכל מצב ותרגול ספירת קלפים בשיטת Hi-Lo. ידידותי לנייד, ללא הרשמה."
              : "Free Blackjack trainer to learn and practice perfect basic strategy. Instant feedback on every hand, a strategy calculator for any table situation, and Hi-Lo card counting drills. Mobile-friendly, no signup."
          }
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SITE}/og.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      {/* HERO ============================================================ */}
      <section
        className="relative px-5 pt-10 pb-8 text-center overflow-hidden"
      >
        <HeroSuit symbol="♠" rotate={-12} top={20} left={-10} />
        <HeroSuit symbol="♦" rotate={18} top={60} right={-20} opacity={0.06} />
        <HeroSuit symbol="♣" rotate={-6} top={220} left={-30} opacity={0.05} />

        <div className="deco-divider mx-auto max-w-[18rem] mb-3">
          <span>House Edge · 0.5%</span>
        </div>

        <h1
          className="font-display text-gold-foil marquee-glow"
          style={{
            fontSize: "clamp(2.6rem, 11vw, 4rem)",
            fontWeight: 800,
            lineHeight: 1.02,
            letterSpacing: "-0.01em",
          }}
        >
          {lng === "he" ? "היט או סטנד" : "Hit or Stand."}
        </h1>
        <h2
          className="font-display italic"
          style={{
            color: "var(--ivory)",
            fontSize: "clamp(1.05rem, 4.5vw, 1.45rem)",
            letterSpacing: "0.04em",
            marginTop: 6,
            opacity: 0.92,
          }}
        >
          {lng === "he"
            ? "אסטרטגיה. לא ניחושים."
            : "Strategy. Not superstition."}
        </h2>

        <p
          className="mt-5 mx-auto"
          style={{
            color: "var(--ivory-dim)",
            fontSize: "1rem",
            maxWidth: "30rem",
            lineHeight: 1.55,
          }}
        >
          {t("seo_home_intro")}
        </p>

        {/* PRIMARY CTAs — casino chip buttons */}
        <div className="mt-8 flex flex-col gap-3 mx-auto" style={{ maxWidth: "22rem" }}>
          <Link to="/calculator" className="chip-btn chip-gold w-full rise-in" style={{ animationDelay: "60ms" }}>
            {lng === "he" ? "מחשבון מיידי" : "Open the Calculator"}
          </Link>
          <Link to="/trainer" className="chip-btn chip-emerald w-full rise-in" style={{ animationDelay: "140ms" }}>
            {lng === "he" ? "התחל לתרגל" : "Start Training"}
          </Link>
          <Link to="/counting" className="chip-btn chip-cobalt w-full rise-in" style={{ animationDelay: "220ms" }}>
            {lng === "he" ? "ספירת קלפים" : "Card Counting"}
          </Link>
        </div>

        <p
          className="font-display italic mt-6"
          style={{
            color: "var(--gold-deep)",
            fontSize: "0.78rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          {lng === "he" ? "חינם · ללא הרשמה · עובד במובייל" : "Free · No signup · Mobile-ready"}
        </p>
      </section>

      {/* THREE FEATURE TILES =========================================== */}
      <section className="px-4 pb-10">
        <div className="deco-divider mb-5 mx-auto" style={{ maxWidth: "28rem" }}>
          <span>{t("seo_home_features_title")}</span>
        </div>

        <div className="grid gap-3" style={{ gridTemplateColumns: "1fr" }}>
          {[
            {
              to: "/trainer",
              eyebrow: lng === "he" ? "תרגול" : "Drill",
              title: t("trainer"),
              body: t("seo_home_feature_trainer"),
              suit: "♠",
              chip: "chip-emerald",
            },
            {
              to: "/calculator",
              eyebrow: lng === "he" ? "מחשבון" : "Lookup",
              title: t("strategy_calculator"),
              body: t("seo_home_feature_calc"),
              suit: "♦",
              chip: "chip-amber",
            },
            {
              to: "/counting",
              eyebrow: lng === "he" ? "ספירה" : "Count",
              title: t("card_counting_trainer"),
              body: t("seo_home_feature_counting"),
              suit: "♣",
              chip: "chip-cobalt",
            },
          ].map((f, idx) => (
            <Link
              key={f.to}
              to={f.to}
              className="deco-frame block group rise-in"
              style={{
                borderRadius: 4,
                textDecoration: "none",
                color: "var(--ivory)",
                animationDelay: `${320 + idx * 90}ms`,
              }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="font-display"
                  style={{
                    color: "var(--gold)",
                    fontSize: "2.6rem",
                    lineHeight: 1,
                    flex: "0 0 auto",
                  }}
                >
                  {f.suit}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    className="font-display italic"
                    style={{
                      color: "var(--gold-light)",
                      fontSize: "0.7rem",
                      letterSpacing: "0.24em",
                      textTransform: "uppercase",
                    }}
                  >
                    {f.eyebrow}
                  </div>
                  <h3
                    className="font-display"
                    style={{
                      fontSize: "1.45rem",
                      fontWeight: 700,
                      color: "var(--ivory)",
                      lineHeight: 1.1,
                      marginTop: 2,
                    }}
                  >
                    {f.title}
                  </h3>
                  <p
                    className="mt-2"
                    style={{
                      color: "var(--ivory-dim)",
                      fontSize: "0.92rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {f.body}
                  </p>
                </div>
                <div
                  className="font-display"
                  style={{
                    color: "var(--gold)",
                    fontSize: "1.4rem",
                    alignSelf: "center",
                  }}
                  aria-hidden="true"
                >
                  →
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SEO PROSE — Why basic strategy =================================== */}
      <section className="px-5 pb-8">
        <div className="cream-surface p-6">
          <div
            className="font-display italic mb-2"
            style={{
              color: "var(--bordeaux)",
              fontSize: "0.72rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
          >
            {t("seo_home_section_title")}
          </div>
          <h2
            className="font-display"
            style={{
              fontSize: "1.75rem",
              fontWeight: 800,
              color: "var(--noir)",
              lineHeight: 1.1,
              marginBottom: "0.75rem",
            }}
          >
            {t("seo_home_why_title")}
          </h2>
          <p style={{ color: "var(--noir-soft)", lineHeight: 1.6 }}>
            {t("seo_home_section_body")}
          </p>
          <p
            style={{
              color: "var(--noir-soft)",
              lineHeight: 1.6,
              marginTop: "1rem",
            }}
          >
            {t("seo_home_why_body")}
          </p>
        </div>
      </section>

      {/* FAQ ============================================================ */}
      <section className="px-5 pb-12">
        <div className="deco-divider mb-5 mx-auto" style={{ maxWidth: "20rem" }}>
          <span>{t("faq_title")}</span>
        </div>
        <div className="space-y-5" style={{ color: "var(--ivory)" }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <details
              key={n}
              className="group faq-card"
              style={{
                borderRadius: 4,
                padding: "0.9rem 1.1rem",
              }}
            >
              <summary
                className="font-display cursor-pointer flex justify-between items-center gap-3"
                style={{
                  fontSize: "1.05rem",
                  fontWeight: 600,
                  color: "var(--ivory)",
                  listStyle: "none",
                }}
              >
                <span>{t(`faq_q${n}`)}</span>
                <span
                  style={{
                    color: "var(--gold)",
                    fontSize: "1.4rem",
                    lineHeight: 1,
                  }}
                  aria-hidden="true"
                >
                  +
                </span>
              </summary>
              <p
                className="mt-3"
                style={{ color: "var(--ivory-dim)", lineHeight: 1.6 }}
              >
                {t(`faq_a${n}`)}
              </p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}
